import { useState, useEffect } from 'react';
import { X, Share } from 'lucide-react';
import { usePwaInstall } from '@/hooks/usePwaInstall';
import { Button } from '@/components/ui/button';

const SHOW_DELAY_MS = 3000;

const PwaInstallBanner = () => {
  const { canShow, platform, triggerInstall, dismiss, isInstalling } = usePwaInstall();
  const [isVisible, setIsVisible] = useState(false);
  const [showSteps, setShowSteps] = useState(false);

  useEffect(() => {
    if (!canShow) return;
    const timer = setTimeout(() => setIsVisible(true), SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [canShow]);

  if (!canShow || !isVisible) return null;

  return (
    <div className="fixed bottom-[4.5rem] left-0 right-0 z-40 px-3 pb-2 animate-slide-up">
      <div className="bg-white rounded-xl shadow-lg border border-border/50 p-3">
        <div className="flex items-center gap-3">
          {/* App icon */}
          <img
            src="/app-assets/66efe52a-f247-4e04-8c15-c6eea1ae49e7.png"
            alt="Birdie Game"
            className="w-10 h-10 rounded-lg flex-shrink-0"
          />

          {/* Text */}
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm text-foreground">Add Birdie Game</p>
            <p className="text-xs text-muted-foreground">
              {platform === 'android'
                ? 'Quick access & works offline'
                : 'Get the full app experience'}
            </p>
          </div>

          {/* Action button */}
          {platform === 'android' ? (
            <Button
              size="sm"
              onClick={triggerInstall}
              disabled={isInstalling}
              className="bg-forest hover:bg-forest/90 text-white flex-shrink-0"
            >
              Install
            </Button>
          ) : (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowSteps(s => !s)}
              className="flex-shrink-0"
            >
              How
            </Button>
          )}

          {/* Dismiss */}
          <button
            onClick={dismiss}
            className="p-1 text-muted-foreground hover:text-foreground flex-shrink-0"
            aria-label="Dismiss install banner"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* iOS Safari instructions */}
        {platform === 'ios' && showSteps && (
          <div className="mt-3 pt-3 border-t border-border/50 space-y-2.5">
            <Step number={1}>
              Tap the <Share className="inline w-4 h-4 -mt-0.5 text-forest" /> Share icon in Safari
            </Step>
            <Step number={2}>
              Scroll down and tap <strong>Add to Home Screen</strong>
            </Step>
            <Step number={3}>
              Tap <strong>Add</strong> in the top right
            </Step>
          </div>
        )}
      </div>
    </div>
  );
};

function Step({ number, children }: { number: number; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2.5 text-sm text-foreground">
      <span className="flex items-center justify-center w-5 h-5 rounded-full bg-forest/10 text-forest text-xs font-semibold flex-shrink-0 mt-0.5">
        {number}
      </span>
      <span>{children}</span>
    </div>
  );
}

export default PwaInstallBanner;
